/**
 * Reveal for LQIP "blur-up" images (JCTCTAS-109, refined 2026-09-05 after a
 * live user test caught two real bugs in the first version).
 *
 * The blur placeholder itself is pure CSS: an inline `background-image` on
 * each `.jctc-lqip` `<img>`'s own wrapper (`.jctc-lqip-bg`, see
 * inc/theme-functions.php's jctc_lqip_style_attr()/jctc_wrap_lqip_svg()) and
 * `.jctc-lqip`'s own default `opacity: 0` (assets/src/scss/base/_base.scss).
 * This module's only job is deciding WHEN to reveal the real photo
 * (`.is-loaded`, which CSS transitions to opacity: 1).
 *
 * Two things this version fixes that the previous one got wrong (confirmed
 * live, not theoretical):
 *
 * 1. The previous version toggled a JS-added "not loaded yet" class instead
 *    of defaulting opacity:0 in CSS -- on a fast/cached load, the browser
 *    had often already painted the real photo (at opacity:1) before this
 *    deferred module even ran, so hiding it at that point just flashed the
 *    already-visible photo OUT before fading it back IN. Opacity:0 is now
 *    the CSS default (see _base.scss's own comment), so a real photo is
 *    NEVER painted before this module explicitly reveals it, regardless of
 *    load speed or cache state -- this module therefore never needs to
 *    "hide" anything, only ever to reveal.
 * 2. The previous version skipped the whole reveal transition for an image
 *    that was already `.complete` at bind time, so the blur was invisible
 *    for the (very common, on a fast connection or a cache hit) case where
 *    that's most images -- confirmed by the user testing repeatedly on the
 *    same pages this session, where only one never-before-cached card ever
 *    showed a blur at all. A fixed minimum visible duration below makes the
 *    blur-then-reveal sequence consistent every time, cache hit or not.
 */
const MIN_VISIBLE_MS = 200;

export default function initLqipReveal() {
    const bind = (img) => {
        if (img.dataset.lqipBound) {
            return;
        }
        img.dataset.lqipBound = '1';

        const boundAt = performance.now();
        const reveal = () => {
            const remaining = MIN_VISIBLE_MS - (performance.now() - boundAt);
            if (remaining > 0) {
                setTimeout(() => img.classList.add('is-loaded'), remaining);
            } else {
                img.classList.add('is-loaded');
            }
        };

        // Still guaranteed to reveal even when already loaded (browser
        // cache, or a same-page re-scan after AJAX insertion of
        // already-cached markup) -- `reveal()`'s own minimum-duration
        // guard still applies, so the blur gets its brief guaranteed
        // moment on screen instead of being skipped entirely. Checking
        // `img.complete` alone (not also `naturalWidth > 0`) matters: an
        // image that had ALREADY failed to load before this script bound to
        // it is also `complete`, and its `error` event already fired in the
        // past and will never fire again -- the `error` listener below only
        // catches a failure that happens AFTER binding, so a pre-failed,
        // already-`complete` image must be caught here too, or it stays
        // hidden behind its blur placeholder forever (JCTCTAS-201).
        if (img.complete) {
            reveal();
            return;
        }

        img.addEventListener('load', reveal, { once: true });
        // A broken/failed image must still reveal (there is nothing left
        // to wait for) rather than sit invisible forever over its own
        // placeholder.
        img.addEventListener('error', reveal, { once: true });
    };

    const scan = (root) => {
        if (root.nodeType !== 1) {
            return;
        }
        if (root.matches?.('.jctc-lqip')) {
            bind(root);
        }
        if (root.querySelectorAll) {
            root.querySelectorAll('.jctc-lqip').forEach(bind);
        }
    };

    scan(document.body);

    // Every AJAX "Load More"/calendar-month-change surface in the theme
    // inserts fresh markup via insertAdjacentHTML/innerHTML -- a plain
    // MutationObserver on <body> catches all of them generically instead
    // of this module needing to know about each handler individually.
    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach(scan);
        });
    }).observe(document.body, { childList: true, subtree: true });
}
